<script setup>
import { vOnClickOutside } from '@vueuse/components'

const props = defineProps({
  autoActive: {
    type: Boolean,
    default: false,
  },
  autoSelect: {
    type: String,
  },
  placeholder: {
    type: String,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Array,
    default: () => {},
  },
  idSelected: {
    type: String,
  },
  selected: {
    type: String,
  },
  labelClass: {
    type: String,
    default: '',
  },
  selectClass: {
    type: String,
    default: '',
  },
  inputClass: {
    type: String,
    default: '',
  },
  dropdownClass: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  labelSelect: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: 'red',
  },
  icon: Boolean,
  required: Boolean,
})
const emits = defineEmits(['onHover', 'onClick', 'onClose', 'onSelect'])
const route = useRoute()
const { defaultTransition } = useTailwindConfig()
const { t } = useI18n()
const selected = ref(props.selected)

const dropdownActive = ref(false)
function dropdownOpen() {
  dropdownActive.value = true
}

function dropdownClose() {
  dropdownActive.value = false
}

function onSelected(item, index, event) {
  if (!props.autoActive)
    selected.value = item
  emits('onSelect', item)
  dropdownClose()
}

const colorClasses = computed(() => {
  switch (props.color) {
    case 'blue':
      return 'hover:ring-blue-700/20 focus:border-blue-700 hover:border-blue-700'
    default:
      return 'hover:ring-red-500/20 focus:border-red-500 hover:border-secondary-red-100'
  }
})

const checkClasses = computed(() => {
  switch (props.color) {
    case 'blue':
      return 'text-blue-700'
    default:
      return 'text-red-500'
  }
})
</script>

<template>
  <div>
    <div v-on-click-outside="dropdownClose" @click="dropdownOpen">
      <label v-if="props.label" :class="labelClass ? labelClass : 'capitalize mb-2 font-medium text-md'" :for="props.label">{{ props.label }} <span v-if="required" class="text-lg leading-[0]">*</span></label>
      <div
        :class="[selectClass ? selectClass : `${colorClasses} ${inputClass} mt-2 hover:ring-3 flex items-center py-3 px-4 text-primary shadow-sm bg-white border border-primary/10 text-sm rounded-md focus:ring-offset-1 focus:ring-2`, defaultTransition]"
      >
        <slot v-if="icon" name="icon" />
        <p :class="labelSelect" class="text-[0.95rem] text-primary font-medium capitalize">
          <span v-if="selected && !autoActive" class="line-clamp-1">{{ selected }}</span>
          <span v-else-if="autoSelect" class="line-clamp-1">{{ autoSelect }}as</span>
          <span v-else class="line-clamp-1 text-slate-300">
            {{ placeholder }}
          </span>
        </p>
        <svg
          class="ml-auto text-[#BCC2D1] transition-all duration-200 ease-in-out"
          xmlns="http://www.w3.org/2000/svg"
          width="22" height="22" viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M8.12 9.29L12 13.17l3.88-3.88a.996.996 0 1 1 1.41 1.41l-4.59 4.59a.996.996 0 0 1-1.41 0L6.7 10.7a.996.996 0 0 1 0-1.41c.39-.38 1.03-.39 1.42 0z"
          />
        </svg>
      </div>
    </div>
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="transform -translate-y-5 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform -translate-y-5 opacity-0"
    >
      <ul v-if="dropdownActive" role="listbox" :class="dropdownClass" class="absolute z-40 block max-h-[450px] w-full overflow-y-auto rounded-sm bg-white pl-0 text-left text-sm text-primary shadow-custom-lg shadow-shadow-light/10 transition duration-200 focus:border-red-500 hover:border-secondary-red-100 focus:ring-2 focus:ring-offset-1 focus:ring-red-500/20">
        <li
          v-for="(item, index) in props.options" :key="item" class="mb-0 flex cursor-pointer items-center px-4 py-3 capitalize [&:first-child]:rounded-tl-md [&:first-child]:rounded-tr-md [&:last-child]:rounded-bl-md [&:last-child]:rounded-br-md hover:bg-secondary-gray-50"
          :class="String(autoSelect) === String(item.value) && !selected || selected === item.value ? 'bg-secondary-gray-100 font-bold' : 'bg-white'" @click="onSelected(item.value, index)"
        >
          <div v-if="item.icon" class="mr-2" v-html="item.icon" />
          <slot v-if="icon" name="icon" />
          <span>{{ t(item.value) }}</span>
          <div v-if="selected ? selected.toLocaleLowerCase() === item.value.toLocaleLowerCase() : String(autoSelect).toLocaleLowerCase() === String(item.value).toLocaleLowerCase" class="ml-auto h-[20px] flex items-center">
            <svg :class="checkClasses" class="transition-transform duration-200 ease-in-out" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="m10.5 16.2l-4-4l1.4-1.4l2.6 2.6l5.6-5.6l1.4 1.4l-7 7Z" /></svg>
          </div>
        </li>
      </ul>
    </transition>
  </div>
</template>
